import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { useAuthStore } from '@/stores/auth'
import { ElMessage } from 'element-plus'
import { requireRelogin } from '@/utils/sessionGuard'

// 统一响应格式
export interface ApiResponse<T = any> {
  code: string
  success: boolean
  message: string
  data: T
}

// APP 本地打包模式下 WebView 源是 https://localhost，API/图片必须指向线上绝对地址；
// Web 同源访问保持空串（相对路径）。CORS 白名单与 img_token sameSite=none 后端已支持。
export const API_HOST = Capacitor.isNativePlatform() ? 'https://dqbotai.cn' : ''
axios.defaults.baseURL = API_HOST

const UPLOADS_PREFIX = '/uploads/'

// 响应里的 /uploads/ 相对路径转绝对（APP 内 <img> 才能打到线上）；
// 回传时剥回相对路径，避免绝对 URL 落库污染 Web 端
const walkUploads = (value: any, toAbsolute: boolean, depth = 0): any => {
  if (depth > 6 || value === null || value === undefined) return value
  if (typeof value === 'string') {
    if (toAbsolute) return value.startsWith(UPLOADS_PREFIX) ? API_HOST + value : value
    return value.startsWith(API_HOST + UPLOADS_PREFIX) ? value.slice(API_HOST.length) : value
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = walkUploads(value[i], toAbsolute, depth + 1)
    return value
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value)) value[k] = walkUploads(value[k], toAbsolute, depth + 1)
    return value
  }
  return value
}

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: `${API_HOST}/api/v1`,
  timeout: 30000,
  // APP 跨站 XHR 必须带凭证,否则登录下发的 img_token cookie(Set-Cookie)被 WebView 忽略,
  // /uploads 受保护图片(报销凭证等)永远 401;PC 同源场景此开关无副作用
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    if (API_HOST && config.data && typeof config.data === 'object') {
      config.data = walkUploads(config.data, false)
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Token 刷新状态：并发请求共享同一次刷新
let isRefreshing = false
let pendingQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = []

const processQueue = (error: any, token: string | null) => {
  pendingQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)))
  pendingQueue = []
}

// 使用 refreshToken 换新 token（独立 axios 实例，避免拦截器递归）
const refreshAccessToken = async (): Promise<string> => {
  const authStore = useAuthStore()
  const res = await axios.post('/api/v1/auth/refresh', { refreshToken: authStore.refreshToken }, { withCredentials: true })
  const newToken = res.data?.data?.token
  if (!newToken) throw new Error('刷新失败')
  authStore.setToken(newToken)
  return newToken
}

// access token 有效期 24h。长时间挂后台/锁屏后用户第一次点击才触发 401→刷新→重放，
// 链路上多两个来回、App 又刚从休眠唤醒，体感就是"点了没反应"。
// 这里在回到前台/可见时提前把临期 token 刷好，用户点的时候直接可用；
// refreshToken 也失效（>7d）则主动弹重新登录框，而不是等点击失败后静默。
const getTokenExpMs = (token: string): number => {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp * 1000
  } catch {
    return 0
  }
}

export const ensureTokenFresh = async (): Promise<void> => {
  const authStore = useAuthStore()
  if (!authStore.token || !authStore.refreshToken) return
  // 距过期不足 5 分钟才刷，其余情况什么都不做（无网络开销）
  if (getTokenExpMs(authStore.token) - Date.now() > 5 * 60 * 1000) return
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push({ resolve: () => resolve(), reject })
    })
  }
  isRefreshing = true
  try {
    const newToken = await refreshAccessToken()
    processQueue(null, newToken)
  } catch (e: any) {
    processQueue(e, null)
    const status = e?.response?.status
    if (status === 400 || status === 401) {
      await requireRelogin()
    }
    // 网络故障/5xx 静默，下次 tick 或真实请求的 401 链路再兜
  } finally {
    isRefreshing = false
  }
}

let keepAliveStarted = false
export const initTokenKeepAlive = () => {
  if (keepAliveStarted) return
  keepAliveStarted = true
  const tick = () => {
    if (document.visibilityState === 'visible') ensureTokenFresh()
  }
  document.addEventListener('visibilitychange', tick)
  window.addEventListener('focus', tick)
  // 前台常驻时每 5 分钟兜底检查一次
  setInterval(tick, 5 * 60 * 1000)
  if (Capacitor.isNativePlatform()) {
    CapApp.addListener('resume', () => ensureTokenFresh())
  }
}

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // blob 下载请求直接返回，不判断 success
    if (response.config.responseType === 'blob') {
      return response
    }
    if (API_HOST && response.data && typeof response.data === 'object') {
      walkUploads(response.data, true)
    }
    const res = response.data
    if (!res.success) {
      ElMessage.error(res.message || '请求失败')
      // 携带业务错误码（如 4601 E淘需重登、4603），供业务代码 catch 后分支处理
      const err: any = new Error(res.message)
      err.code = res.code
      return Promise.reject(err)
    }
    return response
  },
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
    if (error.response?.status === 401) {
      const url = config?.url || ''
      // 登录/刷新接口自身的 401（用户名或密码错误、refreshToken 失效）：
      // 直接展示后端返回的 message，不触发刷新重放和强制登出
      if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
        const data = error.response?.data as any
        const msg = (data && typeof data.message === 'string' && data.message) || '登录失败'
        ElMessage.error(msg)
        return Promise.reject(error)
      }
      const authStore = useAuthStore()
      // 有 refreshToken 且未重试过：自动刷新并重放原请求
      if (authStore.refreshToken && config && !config._retry) {
        if (isRefreshing) {
          // 刷新进行中：排队等待，完成后用新 token 重放
          return new Promise((resolve, reject) => {
            pendingQueue.push({
              resolve: (token: string) => {
                config.headers.Authorization = `Bearer ${token}`
                resolve(request(config))
              },
              reject
            })
          })
        }
        config._retry = true
        isRefreshing = true
        try {
          const newToken = await refreshAccessToken()
          processQueue(null, newToken)
          config.headers.Authorization = `Bearer ${newToken}`
          return request(config)
        } catch (refreshError: any) {
          processQueue(refreshError, null)
          // refreshToken 被后端明确判定无效（400 缺失 / 401 过期）：
          // 弹全局重新登录框而非静默登出，登录成功后携带新 token 重放原请求，功能不中断
          const status = refreshError?.response?.status
          if (status === 400 || status === 401) {
            const ok = await requireRelogin()
            if (ok) {
              config.headers.Authorization = `Bearer ${authStore.token}`
              return request(config)
            }
          } else {
            // 网络故障或服务器 5xx（如服务器重启窗口期）保留登录态，避免抖动踢人下线
            ElMessage.error('网络异常，请稍后重试')
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
      // 无 refreshToken：同样弹重新登录框，成功后重放原请求
      const ok = await requireRelogin()
      if (ok && config) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${authStore.token}`
        return request(config)
      }
    } else if (error.response?.status === 403) {
      const msg = (error.response?.data as any)?.message || '无此权限'
      ElMessage.error(msg)
    } else {
      // 优先展示后端返回的业务错误信息（如 400 参数/状态校验失败原因）
      const data = error.response?.data as any
      const msg = (data && typeof data.message === 'string' && data.message) || error.message || '网络错误'
      ElMessage.error(msg)
    }
    return Promise.reject(error)
  }
)

export default request
